import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import { caisseAPI, transactionAPI } from '../../services/api';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import Rows from '../../components/ui/Rows';
import Filter from '../../components/ui/Filter';

const ACTIONS_WIDTH = '120px';

const HEADER_HEIGHT = 76;

const contentContainerSx = {
  width: '100%',
  margin: '0 auto',
  paddingLeft: { xs: '26px', sm: '42px' },
  paddingRight: { xs: '26px', sm: '42px' },
};

const NouveauRapprochementStep1 = () => {
  const history = useHistory();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const [filters, setFilters] = useState(() => ({
    searchTerm: query.get('search') || '',
    // Support multiple caisse IDs in query string (repeated keys or comma-separated)
    caisseId: (function() {
      const rawAll = query.getAll('caisseId');
      const single = query.get('caisseId');
      if (rawAll && rawAll.length > 0) {
        // If a single entry contains commas, split it
        if (rawAll.length === 1 && rawAll[0] && rawAll[0].includes(',')) return rawAll[0].split(',').map(s => Number(s));
        return rawAll.map(s => Number(s));
      }
      if (single) {
        if (single.includes(',')) return single.split(',').map(s => Number(s));
        return Number(single);
      }
      return null;
    })(),
    dateRange:
      query.get('fromDate') && query.get('toDate')
        ? { start: new Date(query.get('fromDate')), end: new Date(query.get('toDate')) }
        : null,
  }));

  const [versements, setVersements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [caisses, setCaisses] = useState([]);
  const [pagination, setPagination] = useState({ page: 0, size: 20 });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('DESC');

  useEffect(() => {
    caisseAPI
      .getForFilters(false)
      .then((res) => setCaisses((res.data || []).filter((c) => c.type === 'MOBILE')))
      .catch((err) => console.error('Error loading caisses:', err));
  }, []);

  const fetchVersements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // If multiple caisse IDs selected, backend endpoint accepts a single caisseId,
      // so fetch each selected caisse separately and merge results client-side.
      if (Array.isArray(filters.caisseId) && filters.caisseId.length > 0) {
        const calls = filters.caisseId.map((id) => {
          const params = {};
          if (id) params.caisseId = id;
          if (filters.dateRange?.start) params.fromDate = filters.dateRange.start.toLocaleDateString('en-CA');
          if (filters.dateRange?.end) params.toDate = filters.dateRange.end.toLocaleDateString('en-CA');
          return transactionAPI.getVersementsNonVerifies(params);
        });
        const responses = await Promise.all(calls);
        const combined = responses.flatMap((r) => r.data || []);
        // Deduplicate by transaction id
        const uniq = Array.from(new Map(combined.map(item => [item.id, item])).values());
        setVersements(uniq);
      } else {
        const params = {};
        if (filters.caisseId) params.caisseId = filters.caisseId;
        if (filters.dateRange?.start) params.fromDate = filters.dateRange.start.toLocaleDateString('en-CA');
        if (filters.dateRange?.end) params.toDate = filters.dateRange.end.toLocaleDateString('en-CA');
        const res = await transactionAPI.getVersementsNonVerifies(params);
        setVersements(res.data || []);
      }
      setPagination((prev) => ({ ...prev, page: 0 }));
    } catch (err) {
      console.error('Error fetching versements:', err);
      setError('Échec du chargement des versements');
    } finally {
      setLoading(false);
    }
  }, [filters.caisseId, filters.dateRange]);

  useEffect(() => {
    fetchVersements();
  }, [fetchVersements]);

  const updateFilters = (updated) => {
    const params = new URLSearchParams();
    if (updated.searchTerm) params.set('search', updated.searchTerm);
    if (updated.caisseId) {
      if (Array.isArray(updated.caisseId)) {
        updated.caisseId.forEach(id => params.append('caisseId', id));
      } else {
        params.set('caisseId', updated.caisseId);
      }
    }
    if (updated.dateRange?.start) params.set('fromDate', updated.dateRange.start.toLocaleDateString('en-CA'));
    if (updated.dateRange?.end) params.set('toDate', updated.dateRange.end.toLocaleDateString('en-CA'));
    history.replace({ pathname: location.pathname, search: params.toString() });
    setFilters(updated);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleReset = () => {
    history.replace({ pathname: location.pathname, search: '' });
    setFilters({ searchTerm: '', caisseId: null, dateRange: null });
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleSort = (field, direction) => {
    setSortBy(field);
    setSortDirection(direction);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} - ${hours}:${minutes}`;
  };

  const searchFiltered = filters.searchTerm
    ? versements.filter((v) => {
        const term = filters.searchTerm.replace(/[\s,]/g, '').toLowerCase();
        return (
          v.code?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          String(v.amount ?? '').replace('.', ',').includes(term)
        );
      })
    : versements;

  const sorted = [...searchFiltered].sort((a, b) => {
    if (sortBy === 'createdAt') {
      return sortDirection === 'DESC'
        ? new Date(b.createdAt) - new Date(a.createdAt)
        : new Date(a.createdAt) - new Date(b.createdAt);
    }
    return 0;
  });

  const total = sorted.length;
  const paginatedData = sorted.slice(
    pagination.page * pagination.size,
    (pagination.page + 1) * pagination.size
  );

  const transformedData = paginatedData.map((v) => ({
    id: v.id,
    code: v.code,
    createdAt: v.createdAt,
    // The backend returns a `caisse` object; prefer its name when available
    caisseName: v.caisse?.name || v.caisseName || '',
    amount: v.amount,
    originalData: v,
  }));

  const columns = [
    {
      key: 'code',
      label: 'Versement ID',
      sortable: false,
      render: (v) => (
        <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, color: '#38485C' }}>
          {v}
        </span>
      ),
    },
    {
      key: 'createdAt',
      label: 'Date',
      sortable: true,
      render: (v) => formatDate(v),
    },
    { key: 'caisseName', label: 'Caisse', sortable: false },
    {
      key: 'amount',
      label: 'Montant',
      sortable: false,
      render: (v) => (
        <span style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 700, lineHeight: '19px', color: '#027BC6' }}>
          {formatAmountForDisplay(v, false).text}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: ACTIONS_WIDTH,
      render: (_, row) => (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            history.push(`/rapprochements/nouveau/${row.originalData.id}`, { versement: row.originalData });
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '85px',
            height: '32px',
            background: '#FFFFFF',
            boxShadow: '0px 6px 26px #626B7D12',
            border: '1px solid #75808E14',
            borderRadius: '12px',
            cursor: 'pointer',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 600,
            color: '#75808E',
            userSelect: 'none',
            transition: 'background 0.15s, border-color 0.15s, color 0.15s',
            '&:hover': { background: '#EBF5FF', borderColor: '#027BC620', color: '#027BC6' },
            '&:active': { background: '#D0EAFB', color: '#025a9c' },
          }}
        >
          Choisir
        </Box>
      ),
    },
  ];

  const filterConfig = [
    { name: 'searchTerm', type: 'search', placeholder: 'Recherche...' },
    {
      name: 'caisseId',
      type: 'dropdown',
      label: 'Caisse',
      placeholder: 'Toutes',
      isMultiSelect: true,
      options: caisses.map((c) => ({ value: c.id, label: c.name })),
    },
    { name: 'dateRange', type: 'daterange', placeholder: 'Date' },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          zIndex: 999,
          background: 'linear-gradient(to bottom, rgba(255,255,255,1), rgba(248,248,248,1), #F8F8F8)',
        }}
      >
        <Box sx={{ ...contentContainerSx, paddingTop: '24px', paddingBottom: '8px' }}>
          {/* Breadcrumb + Title */}
          <Box sx={{ marginBottom: '16px' }}>
            <p
              style={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '14px',
                color: '#919191',
                margin: '0 0 4px 0',
              }}
            >
              Nouveau rapprochement
            </p>
            <h1
              style={{
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '28px',
                lineHeight: '32px',
                color: '#38485C',
                opacity: 0.88,
                margin: 0,
              }}
            >
              <span style={{ color: '#027BC6' }}>Étape 1 :</span> Sélection du versement
            </h1>
          </Box>

          {/* Filters */}
          <Box sx={{ marginBottom: '8px', position: 'relative', zIndex: 1001 }}>
            <Filter
              filters={filterConfig}
              filterValues={filters}
              onFilterChange={(name, value) => updateFilters({ ...filters, [name]: value || null })}
              onReset={handleReset}
              columns={3}
            />
          </Box>
        </Box>

        {/* Column headers */}
        <Box sx={contentContainerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '16px',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat',
            }}
          >
            {columns.map((col, idx) => {
              const isActions = col.key === 'actions';
              const isAmount = col.key === 'amount' || col.key === 'montant';
              const isSorted = sortBy === col.key;
              return (
                <Box
                  key={idx}
                  sx={{
                    paddingLeft: '24px',
                    ...(isActions
                      ? { flex: 'none', width: ACTIONS_WIDTH, textAlign: 'center' }
                      : isAmount
                      ? { flex: 'none', width: '200px' }
                      : { flex: 1, minWidth: '150px' }),
                  }}
                >
                  {col.sortable ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        color: isSorted ? '#027BC6' : '#919191',
                        '&:hover': { color: '#027BC6' },
                        transition: 'color 0.2s',
                      }}
                      onClick={() =>
                        handleSort(col.key, sortBy === col.key && sortDirection === 'ASC' ? 'DESC' : 'ASC')
                      }
                    >
                      <span>{col.label}</span>
                      {isSorted && (
                        <img
                          src={chevronUpIcon}
                          alt=""
                          style={{
                            width: '12px',
                            height: '12px',
                            transform: sortDirection === 'DESC' ? 'rotate(180deg)' : 'none',
                            filter:
                              'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)',
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <span>{col.label}</span>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Box sx={contentContainerSx}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Rows
            columns={columns}
            data={transformedData}
            pagination={{ page: pagination.page, size: pagination.size, total }}
            onPageChange={(p) => setPagination((prev) => ({ ...prev, page: p }))}
            onRowsPerPageChange={(s) => setPagination((prev) => ({ ...prev, size: s, page: 0 }))}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            wrapLayout={false}
            expandable={false}
            hideHeader={true}
            containerPadding={0}
          />
        )}
      </Box>
    </Box>
  );
};

export default NouveauRapprochementStep1;
